"use client";

import UserAvatar from "@/components/Avatar/UserAvatar";
import Button from "@/components/ui/Button/Button";
import nProgress from "nprogress";
import { useState, useRef } from "react";
import { toast } from "react-toastify";
import { useTranslations } from "next-intl";
import { useSession } from "next-auth/react";

export default function AddComment(props) {
    const [comment, setComment] = useState("");
    const [isFocused, setIsFocused] = useState(false);
    const { data: session } = useSession();
    const textareaRef = useRef(null);
    const t = useTranslations();

    const add = async (e) => {
        e.preventDefault();

        if (!comment) return;

        nProgress.start();

        if (props.reply) {
            // Add reply
            const res = await fetch("/api/article/comment", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    comment,
                    articleId: props.article.id,
                    commentId: props.commentId,
                    revalidate: true,
                }),
            });

            if (!res.ok) {
                nProgress.done();
                toast.error(t("AddComment_failedToAddComment"));
                return;
            }

            const data = await res.json();

            nProgress.done();
            setComment("");
            toast.success(t("AddComment_commentAddedSuccessfully"));
            props.addComment(data.comment);
        } else {
            // Add comment
            const res = await fetch("/api/article/comment", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    comment,
                    articleId: props.article.id,
                    revalidate: true,
                }),
            });

            if (!res.ok) {
                nProgress.done();
                toast.error(t("AddComment_failedToAddComment"));
                return;
            }

            const data = await res.json();

            nProgress.done();
            setComment("");
            setIsFocused(false);
            toast.success(t("AddComment_commentAddedSuccessfully"));
            props.addComment(data.comment);
        }
    };

    // Reply mode - compact but elegant design
    if (props.reply) {
        return (
            <form className="mt-1" onSubmit={add}>
                <div className="relative rounded-xl bg-white dark:bg-bgDark-400 border border-gray-200 dark:border-borderDark focus-within:border-primary/50 focus-within:ring-2 focus-within:ring-primary/10 transition-all overflow-hidden">
                    {/* Mini header with avatar */}
                    <div className="flex items-center gap-2 px-3 pt-3 pb-1">
                        {session?.user?.photo ? (
                            <UserAvatar plain
                                user={session.user}
                                alt=""
                                className="w-6 h-6 rounded-full object-cover"
                            />
                        ) : (
                            <div className="w-6 h-6 rounded-full bg-gradient-to-br from-primary/80 to-primary flex items-center justify-center text-white text-xs font-medium">
                                {session?.user?.firstName?.[0] || "?"}
                            </div>
                        )}
                        <span className="text-xs text-gray-400 dark:text-grayDark-500">
                            {t("AddComment_replyingTo")}
                        </span>
                    </div>

                    <textarea
                        className="w-full h-20 bg-transparent dark:text-white px-3 py-2 font-sans text-sm leading-relaxed placeholder:text-gray-400 dark:placeholder:text-grayDark-500 outline-hidden resize-none"
                        placeholder={t("AddComment_replyPlaceholder")}
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        autoFocus
                    />

                    {/* Actions footer */}
                    <div className="flex items-center justify-end gap-2 px-3 pb-3">
                        <button
                            type="button"
                            onClick={() => props.setReplyOpen(false)}
                            className="px-3 py-1.5 text-xs font-medium text-gray-500 hover:text-gray-700 dark:text-grayDark-400 dark:hover:text-grayDark-200 transition-colors"
                        >
                            {t("AddComment_cancel")}
                        </button>
                        <button
                            type="submit"
                            disabled={!comment.trim()}
                            className={`
                                px-4 py-1.5 text-xs font-medium rounded-lg transition-all btn-3d
                                ${comment.trim()
                                    ? "bg-primary text-white hover:bg-primary/90 lip-orange"
                                    : "bg-gray-100 dark:bg-bgDark-300 text-gray-400 cursor-not-allowed"
                                }
                            `}
                        >
                            {t("AddComment_submit")}
                        </button>
                    </div>
                </div>
            </form>
        );
    }

    // Main comment form - enhanced design
    return (
        <form
            className="mt-6 mb-8"
            onSubmit={add}
        >
            <div
                className={`
                    relative rounded-2xl transition-all duration-300
                    ${isFocused || comment
                        ? "bg-white dark:bg-bgDark-400 shadow-lg shadow-gray-200/50 dark:shadow-black/20 border-2 border-primary/20"
                        : "bg-gradient-to-br from-slate-50 to-slate-100/80 dark:from-bgDark-400 dark:to-bgDark-300 border-2 border-transparent hover:border-gray-200 dark:hover:border-borderDark"
                    }
                `}
                onClick={() => textareaRef.current?.focus()}
            >
                {/* Header with avatar and prompt */}
                <div className="flex items-center gap-3 p-4 pb-2">
                    {session?.user?.photo ? (
                        <UserAvatar plain
                            user={session.user}
                            alt=""
                            className="w-8 h-8 rounded-full object-cover ring-2 ring-white dark:ring-bgDark-300 shadow-sm"
                        />
                    ) : (
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary/80 to-primary flex items-center justify-center text-white text-sm font-medium shadow-sm">
                            {session?.user?.firstName?.[0] || "?"}
                        </div>
                    )}
                    <span className={`text-sm transition-colors duration-200 ${isFocused || comment ? "text-gray-500 dark:text-grayDark-400" : "text-gray-400 dark:text-grayDark-500"}`}>
                        {isFocused || comment
                            ? t("AddComment_writingAs", { name: session?.user?.firstName || "Vous" })
                            : t("AddComment_joinConversation")
                        }
                    </span>
                </div>

                {/* Textarea */}
                <textarea
                    ref={textareaRef}
                    className={`
                        w-full bg-transparent dark:text-white px-4 pb-4 pt-1 font-sans text-[15px] leading-relaxed
                        placeholder:text-gray-400 dark:placeholder:text-grayDark-500
                        outline-hidden resize-none transition-all duration-300
                        ${isFocused || comment ? "h-28" : "h-16"}
                    `}
                    placeholder={t("AddComment_commentPlaceholder")}
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    onFocus={() => setIsFocused(true)}
                    onBlur={() => !comment && setIsFocused(false)}
                />

                {/* Footer with submit button */}
                <div className={`
                    flex items-center justify-end px-4 pb-4 transition-all duration-300
                    ${isFocused || comment ? "opacity-100" : "opacity-0 h-0 pb-0 overflow-hidden"}
                `}>
                    <Button
                        disabled={!comment.trim()}
                        className={!comment.trim() ? "opacity-50 cursor-not-allowed" : ""}
                    >
                        {t("AddComment_submit")}
                    </Button>
                </div>
            </div>
        </form>
    );
}
